// Global
import { Text } from '@sitecore-content-sdk/nextjs';
import React, { JSX, useCallback, useRef } from 'react';
import { tv } from 'tailwind-variants';

// Local
import { Lists } from '.generated/Feature.CW.model';
import RichTextWrapper from 'helpers/SitecoreWrappers/RichTextWrapper/RichTextWrapper';
import { withStandardComponentWrapper } from 'helpers/HOC';
import { SvgIcon } from 'helpers/SvgIcon';
import { useScrollElementIntoView } from 'lib/hooks/useScrollElementIntoView';
import { useAccordionItemContext } from 'helpers/Context/AccordionContext';
import classNames from 'classnames';
import useIsEditing from 'lib/hooks/useIsEditing';

export type AccordionItemProps = Lists.AccordionItem_Component;

const AccordionItem = (props: AccordionItemProps): JSX.Element => {
  const { content, heading } = props?.fields || {};
  const { uid } = props?.rendering || {};
  const isEditing = useIsEditing();

  const accordionId = `accordion-${uid}`;
  /*
   * REFS
   */

  const accordionItemButtonRef = useRef<HTMLButtonElement>(null);

  const accordionItemBodyRef = useRef<HTMLDivElement>(null);

  /*
   * State
   */

  const { isOpening, toggleOpen, isVisible, scrollToOpenPanel } = useAccordionItemContext(
    uid ?? '',
    accordionItemBodyRef
  );

  /*
   * Convenience Methods
   */

  React.useEffect(() => {
    const bodyEl = accordionItemBodyRef.current;
    if (!bodyEl) return;

    // Function to update tabindex
    const updateTabIndex = () => {
      const nodes = bodyEl.querySelectorAll('a,button');
      nodes.forEach((el) => {
        el.setAttribute('tabindex', isOpening ? '0' : '-1');
      });
    };

    // Initial run
    updateTabIndex();

    // Set up MutationObserver
    const observer = new MutationObserver(() => {
      updateTabIndex();
    });

    observer.observe(bodyEl, {
      childList: true,
      subtree: true,
    });

    // Cleanup
    return () => observer.disconnect();
  }, [isOpening, content?.value]);

  const toggleAccordion = useCallback(() => {
    toggleOpen();
  }, [toggleOpen]);

  /*
   * Lifecycle
   */

  useScrollElementIntoView(accordionItemButtonRef.current, {
    stickyHeaderId: 'header',
    condition: scrollToOpenPanel && isOpening,
    animationElement: accordionItemBodyRef.current,
    scrollTargetId: accordionId,
  });

  /*
   * Rendering
   */

  if (heading?.value === '' && !isEditing) {
    return <></>;
  }

  const {
    base,
    buttonWrapper,
    iconWrapper,
    contentContainer,
    contentContainerInner,
    richTextWrapper,
  } = TAILWIND_VARIANTS({
    isOpening,
    isVisible,
  });

  return (
    <div
      className={base()}
      data-component="authorable/shared/lists/accordionitem"
      key={accordionId}
    >
      <div className="py-1">
        <button
          aria-expanded={isOpening}
          className={buttonWrapper()}
          onClick={toggleAccordion}
          ref={accordionItemButtonRef}
          type="button"
        >
          <Text field={heading} tag="h3" />
          <span className={iconWrapper()}>
            <SvgIcon
              icon={!isOpening ? 'plus' : 'minus'}
              viewBox={!isOpening ? '0 0 24 24' : '0 0 21 5'}
              size="custom"
              className={classNames(
                'text-blue-base transition-all duration-300 !w-[28px] !h-[28px]',
                !isOpening ? 'rotate-90' : 'rotate-0'
              )}
            />
          </span>
        </button>
        <div
          aria-labelledby={accordionId}
          className={classNames(contentContainer(), 'accordion-item-body')}
          role="region"
          ref={accordionItemBodyRef}
        >
          <div className={contentContainerInner()}>
            <RichTextWrapper
              aria-required={isOpening}
              className={richTextWrapper()}
              field={content}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export const Default = withStandardComponentWrapper(AccordionItem);

const TAILWIND_VARIANTS = tv({
  defaultVariants: {
    isOpen: false,
    isVisible: true,
  },
  slots: {
    base: [
      'relative',
      'before:h-2',
      'before:-top-1',
      'before:w-full',
      'before:bg-gray-50',
      'before:rounded-md',
      'before:absolute',
      'after:last:h-2',
      'after:last:-bottom-1',
      'after:last:w-full',
      'after:last:bg-gray-50',
      'after:last:rounded-md',
      'after:last:absolute',
    ],
    buttonWrapper: [
      'cursor-pointer',
      'duration-300',
      'flex',
      'items-center',
      'justify-between',
      'gap-6',
      'py-16',
      'px-8',
      'font-centuryGothic',
      'font-bold',
      'text-[24px] md:text-26',
      'transition-none',
      'w-full',
      'focus:outline-theme-brand3',
      'text-start',
      'active:bg-blue-50',
      'leading-31',
    ],
    contentContainer: ['duration-300', 'ease-in-out', 'grid', 'transition-all'],
    contentContainerInner: ['overflow-hidden'],
    iconWrapper: ['transform', 'transition-transform'],
    richTextWrapper: ['font-normal', 'mb-8', 'p-8'],
  },
  variants: {
    isOpening: {
      false: {
        contentContainer: ['grid-rows-[0fr]', 'opacity-0'],
        iconWrapper: [],
      },
      true: {
        contentContainer: ['grid-rows-[1fr]', 'opacity-100'],
        iconWrapper: ['rotate-180'],
      },
    },

    isVisible: {
      false: ['max-sm:hidden'],
      true: [],
    },
  },
});
